/**
 * Timezone-safe date logic.
 *
 * **The rule this module exists to enforce:** never read the host's timezone.
 *
 * A workout logged at 21:00 in Johannesburg (UTC+2) belongs to that day. If the
 * PC were set to UTC, `new Date(...).getDate()` would report the *next* day and
 * the session would land on the wrong date in the calendar and in every
 * statistic derived from it. The iPhone, in a different timezone again, would
 * disagree with the PC about the same workout.
 *
 * Every function here therefore takes an explicit `tzOffsetMin` — the offset
 * recorded on the session itself — and does its arithmetic in UTC after
 * shifting by that offset. The host's `Date` is used only as integer
 * arithmetic on an epoch value; no local-time getter is ever called.
 */

/** Minutes in one day. */
export const MINUTES_PER_DAY = 1440;
const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;

export class InvalidDateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidDateError';
  }
}

/** Offsets outside ±14:00 do not exist; anything else indicates corrupt data. */
export function isValidTzOffset(tzOffsetMin: number): boolean {
  return Number.isInteger(tzOffsetMin) && tzOffsetMin >= -840 && tzOffsetMin <= 840;
}

function assertOffset(tzOffsetMin: number): void {
  if (!isValidTzOffset(tzOffsetMin)) {
    throw new InvalidDateError(
      `Timezone offset must be a whole number of minutes within ±840, received: ${String(tzOffsetMin)}`,
    );
  }
}

function assertEpoch(epochMs: number): void {
  if (!Number.isFinite(epochMs)) {
    throw new InvalidDateError(`Timestamp must be a finite number, received: ${String(epochMs)}`);
  }
}

const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

/** Strict 'YYYY-MM-DD' check, including real calendar validity. */
export function isValidLocalDate(localDate: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(localDate)) return false;
  const [y, m, d] = localDate.split('-').map(Number) as [number, number, number];
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  // Round-trip through UTC to reject 2026-02-30 and similar.
  const asUtc = Date.UTC(y, m - 1, d);
  const back = new Date(asUtc);
  return back.getUTCFullYear() === y && back.getUTCMonth() === m - 1 && back.getUTCDate() === d;
}

/**
 * The local calendar date for an instant, in the given offset.
 *
 * Independent of the host timezone: the epoch is shifted by the offset and then
 * read with UTC getters.
 */
export function localDateFromEpoch(epochMs: number, tzOffsetMin: number): string {
  assertEpoch(epochMs);
  assertOffset(tzOffsetMin);

  const shifted = new Date(epochMs + tzOffsetMin * MS_PER_MINUTE);
  return `${pad(shifted.getUTCFullYear(), 4)}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}

/** The local wall-clock time for an instant, as 'HH:MM'. */
export function localTimeFromEpoch(epochMs: number, tzOffsetMin: number): string {
  assertEpoch(epochMs);
  assertOffset(tzOffsetMin);

  const shifted = new Date(epochMs + tzOffsetMin * MS_PER_MINUTE);
  return `${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`;
}

/** Epoch milliseconds for 00:00 on a local date in the given offset. */
export function startOfLocalDay(localDate: string, tzOffsetMin: number): number {
  assertOffset(tzOffsetMin);
  if (!isValidLocalDate(localDate)) {
    throw new InvalidDateError(`Expected a valid 'YYYY-MM-DD' date, received: '${localDate}'`);
  }
  const [y, m, d] = localDate.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d) - tzOffsetMin * MS_PER_MINUTE;
}

/** Epoch milliseconds for the instant immediately after a local day ends. */
export function endOfLocalDayExclusive(localDate: string, tzOffsetMin: number): number {
  return startOfLocalDay(localDate, tzOffsetMin) + MS_PER_DAY;
}

/** Whether two instants fall on the same local date in the same offset. */
export function isSameLocalDate(aEpochMs: number, bEpochMs: number, tzOffsetMin: number): boolean {
  return localDateFromEpoch(aEpochMs, tzOffsetMin) === localDateFromEpoch(bEpochMs, tzOffsetMin);
}

/** Whole local days between two dates; negative when `to` precedes `from`. */
export function daysBetweenLocalDates(from: string, to: string): number {
  if (!isValidLocalDate(from) || !isValidLocalDate(to)) {
    throw new InvalidDateError(`Expected valid 'YYYY-MM-DD' dates, received '${from}' and '${to}'`);
  }
  // Offset cancels out, so any consistent offset gives the same answer.
  return Math.round((startOfLocalDay(to, 0) - startOfLocalDay(from, 0)) / MS_PER_DAY);
}

/** Add (or subtract) whole days to a local date. */
export function addLocalDays(localDate: string, days: number): string {
  if (!Number.isInteger(days)) {
    throw new InvalidDateError(`Day count must be a whole number, received: ${String(days)}`);
  }
  return localDateFromEpoch(startOfLocalDay(localDate, 0) + days * MS_PER_DAY, 0);
}

/**
 * ISO-8601 weekday for a local date: Monday = 1 … Sunday = 7.
 *
 * Matches `plan_day_schedule.weekday`, and is computed from the date string
 * rather than a `Date` local getter so the host timezone cannot shift it.
 */
export function weekdayFromLocalDate(localDate: string): number {
  const utcDay = new Date(startOfLocalDay(localDate, 0)).getUTCDay();
  // getUTCDay: Sunday = 0 … Saturday = 6. ISO: Monday = 1 … Sunday = 7.
  return utcDay === 0 ? 7 : utcDay;
}

/** Elapsed whole seconds between two instants. Never negative. */
export function elapsedSeconds(fromEpochMs: number, toEpochMs: number): number {
  assertEpoch(fromEpochMs);
  assertEpoch(toEpochMs);
  return Math.max(0, Math.floor((toEpochMs - fromEpochMs) / 1000));
}

/**
 * Format a duration as 'H:MM:SS', or 'M:SS' when under an hour.
 * Locale-independent by construction — no `Intl`, no host formatting.
 */
export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    throw new InvalidDateError(`Duration must be a non-negative number, received: ${String(totalSeconds)}`);
  }
  const seconds = Math.floor(totalSeconds);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0 ? `${String(h)}:${pad(m)}:${pad(s)}` : `${String(m)}:${pad(s)}`;
}
